// dagLenses — the cone "lenses" of the Thread-V5 DAG explorer, as PURE
// functions over the real provenance graph (via `@flexcompute/thread-graph`).
//
// This is the product realisation of the dc-runtime design prototype's
// `coneMode` logic (see /dag_design/component.js). The prototype computed a
// per-node `role` ('sel' | 'up' | 'down' | 'shared' | 'partial' | 'match' |
// 'off' …) inside `renderVals()` for four analysis modes laid over the SAME
// cone. Here each mode is a pure function `(index, root(s)) → LensResult` that
// the React layer can consume to decide which nodes/edges to LIGHT vs DIM, plus
// a small `summary` of counts for the readout strip.
//
// The four lenses (matching the prototype + the feature inventory):
//   • trace  — upstream cone ∪ (optionally) downstream cone, with an up/down/both
//              direction toggle. Roles: sel / up / down / off.
//   • impact — downstream-only: everything THIS artifact feeds (what goes stale
//              if it changes). Roles: sel / down / off.
//   • diff   — N-way set-intersection of ancestry across ≥2 picked artifacts:
//              `shared` = in EVERY pick's lineage (the common source), `partial`
//              = in SOME but not all. Roles: selpick / shared / partial / off.
//   • filter — highlight one node KIND across the whole graph, dim the rest.
//              Roles: match / off (with a `mesh` substring convenience, matching
//              the prototype's `fk==='mesh' && /mesh/.test(k)`).
//
// Every lens returns the SAME shape (`LensResult`) so the renderer can treat
// them uniformly: a `roleByNode` map, `litNodes`/`dimNodes` partition, the set
// of lit edges (both endpoints lit AND on-role), and a `summary` record.
//
// Pure + framework-agnostic: no React, no DOM, no design tokens. The renderer
// maps `NodeRole` → --v5-* tokens; this module never names a colour.

import {
  ancestors,
  descendants,
  type GraphIndex,
} from "@flexcompute/thread-graph";

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

/** The four cone analysis lenses (the prototype's `coneMode`). */
export type DagLensMode = "trace" | "impact" | "diff" | "filter";

/** Trace direction toggle (the prototype's `traceMode`). */
export type TraceDirection = "up" | "down" | "both";

/**
 * A node's role under the active lens. Drives highlight vs dim in the renderer.
 *   - sel:     the focused root (trace / impact).
 *   - selpick: one of the artifacts picked for a diff.
 *   - up:      an upstream ancestor of the root (trace).
 *   - down:    a downstream descendant of the root (trace / impact).
 *   - shared:  in EVERY picked artifact's lineage (diff — the common source).
 *   - partial: in SOME (not all) picked artifacts' lineage (diff).
 *   - match:   matches the chosen filter kind (filter).
 *   - off:     dimmed — not on-role for this lens.
 */
export type NodeRole =
  | "sel"
  | "selpick"
  | "up"
  | "down"
  | "shared"
  | "partial"
  | "match"
  | "off";

/** An edge's role — derived from its endpoints' roles. `off` ⇒ dimmed. */
export type EdgeRole = "up" | "down" | "shared" | "partial" | "match" | "off";

/** A lit edge under the active lens (an index pair into `graph.edges`). */
export interface LensEdge {
  /** Index into the source graph's `edges` array. */
  index: number;
  source: string;
  target: string;
  role: EdgeRole;
}

/** Per-lens readout counts for the summary strip. */
export interface LensSummary {
  mode: DagLensMode;
  /** Number of LIT (on-role, non-`off`) nodes. */
  lit: number;
  /** Number of dimmed (`off`) nodes. */
  dim: number;
  /**
   * Mode-specific counts, mirroring the prototype's readout vars:
   *   trace:  { upstream, downstream }      (excludes the root itself)
   *   impact: { downstream, plots }          (downstream count + plot/run sinks)
   *   diff:   { shared, partial, picks }
   *   filter: { matched, kind }
   */
  detail: Record<string, number | string>;
  /** A human-readable one-liner for the caption (token-free). */
  note: string;
}

/** The uniform result every lens returns. */
export interface LensResult {
  mode: DagLensMode;
  /** id → role. Every node in the graph appears (defaults to `off`). */
  roleByNode: Map<string, NodeRole>;
  /** Ids whose role is NOT `off` (the highlighted set). */
  litNodes: Set<string>;
  /** Ids whose role IS `off` (the dimmed set). */
  dimNodes: Set<string>;
  /** Lit edges (both endpoints lit + a coherent shared role). */
  litEdges: LensEdge[];
  summary: LensSummary;
}

/** Options shared across the lens entry point. */
export interface LensOptions {
  /** trace only — which way the cone walks. Default "both". */
  traceDirection?: TraceDirection;
  /** Max hops for ancestry/descendant walks. Default 64 (effectively whole). */
  depth?: number;
  /**
   * filter only — the node KIND (canonical `type`) to highlight. When omitted,
   * nothing matches and everything dims.
   */
  filterKind?: string;
}

const DEFAULT_DEPTH = 64;

// ─────────────────────────────────────────────────────────────────────────────
// Internals — partition + edge derivation
// ─────────────────────────────────────────────────────────────────────────────

/** Every node id in the index, so the partition is exhaustive. */
function allNodeIds(index: GraphIndex): string[] {
  return [...index.nodeById.keys()];
}

/**
 * Materialise a `LensResult` from a role assigner. Walks every node once,
 * partitions lit/dim, then derives lit edges (both endpoints lit + a single
 * coherent edge role). Edges across roles (e.g. one `up`, one `down`) only
 * light when both endpoints carry the SAME on-role — matching the prototype's
 * edge logic (`showUp && up.has(a) && up.has(b)` etc.).
 */
function materialise(
  mode: DagLensMode,
  index: GraphIndex,
  edges: ReadonlyArray<{ source: string; target: string }>,
  roleOf: (id: string) => NodeRole,
  summaryFor: (lit: Set<string>, dim: Set<string>, roleByNode: Map<string, NodeRole>) => LensSummary,
): LensResult {
  const roleByNode = new Map<string, NodeRole>();
  const litNodes = new Set<string>();
  const dimNodes = new Set<string>();
  for (const id of allNodeIds(index)) {
    const role = roleOf(id);
    roleByNode.set(id, role);
    if (role === "off") dimNodes.add(id);
    else litNodes.add(id);
  }

  const litEdges: LensEdge[] = [];
  edges.forEach((e, i) => {
    const ra = roleByNode.get(e.source);
    const rb = roleByNode.get(e.target);
    if (ra === undefined || rb === undefined) return;
    const role = edgeRole(ra, rb);
    if (role !== "off") {
      litEdges.push({ index: i, source: e.source, target: e.target, role });
    }
  });

  return {
    mode,
    roleByNode,
    litNodes,
    dimNodes,
    litEdges,
    summary: summaryFor(litNodes, dimNodes, roleByNode),
  };
}

/**
 * The edge's role from its two endpoints. An edge lights only when both ends
 * share a coherent traversal role; the endpoints' own `sel`/`selpick` markers
 * stand in for membership in the relevant set.
 */
function edgeRole(a: NodeRole, b: NodeRole): EdgeRole {
  const upish = (r: NodeRole) => r === "up" || r === "sel";
  const downish = (r: NodeRole) => r === "down" || r === "sel";
  const sharedish = (r: NodeRole) => r === "shared" || r === "selpick";
  const someish = (r: NodeRole) =>
    r === "shared" || r === "partial" || r === "selpick";

  // filter: both endpoints matched.
  if (a === "match" && b === "match") return "match";
  // diff: both shared (the common-ancestry backbone) → shared; else if both are
  // in some pick's lineage → partial.
  if (sharedish(a) && sharedish(b)) return "shared";
  if (someish(a) && someish(b)) return "partial";
  // trace/impact upstream backbone.
  if (upish(a) && upish(b)) return "up";
  // trace/impact downstream backbone.
  if (downish(a) && downish(b)) return "down";
  return "off";
}

/** Reachable-id set (ancestors or descendants), INCLUSIVE of the seed. */
function lineageSet(
  index: GraphIndex,
  seed: string,
  dir: "ancestors" | "descendants",
  depth: number,
): Set<string> {
  if (!index.nodeById.has(seed)) return new Set<string>();
  const res = dir === "ancestors" ? ancestors(index, seed, depth) : descendants(index, seed, depth);
  // `ancestors`/`descendants` already include the seed (depth 0).
  return new Set(res.ids);
}

// ─────────────────────────────────────────────────────────────────────────────
// trace lens — upstream ∪ downstream cone with a direction toggle.
// (Consolidates the trace/direction logic that the cone view computes ad hoc.)
// ─────────────────────────────────────────────────────────────────────────────

export function traceLens(
  index: GraphIndex,
  rootId: string,
  graphEdges: ReadonlyArray<{ source: string; target: string }>,
  opts: LensOptions = {},
): LensResult {
  const direction = opts.traceDirection ?? "both";
  const depth = opts.depth ?? DEFAULT_DEPTH;
  const showUp = direction !== "down";
  const showDown = direction !== "up";

  const up = showUp ? lineageSet(index, rootId, "ancestors", depth) : new Set<string>([rootId]);
  const down = showDown ? lineageSet(index, rootId, "descendants", depth) : new Set<string>([rootId]);

  const roleOf = (id: string): NodeRole => {
    if (id === rootId) return "sel";
    if (showUp && up.has(id)) return "up";
    if (showDown && down.has(id)) return "down";
    return "off";
  };

  const summaryFor = (lit: Set<string>, dim: Set<string>): LensSummary => {
    // Exclude the root from the up/down tallies (prototype: `up.size-1`).
    const upstream = Math.max(0, (showUp ? up.size : 1) - 1);
    const downstream = Math.max(0, (showDown ? down.size : 1) - 1);
    const dirWord = direction === "both" ? "up + downstream" : `${direction}stream`;
    return {
      mode: "trace",
      lit: lit.size,
      dim: dim.size,
      detail: { upstream, downstream, direction },
      note:
        `tracing ${dirWord}: ` +
        (showUp ? `${upstream} upstream` : "") +
        (showUp && showDown ? " · " : "") +
        (showDown ? `${downstream} downstream` : ""),
    };
  };

  return materialise("trace", index, graphEdges, roleOf, summaryFor);
}

// ─────────────────────────────────────────────────────────────────────────────
// impact lens — downstream-only: what THIS artifact feeds. Roles: sel / down.
// ─────────────────────────────────────────────────────────────────────────────

export function impactLens(
  index: GraphIndex,
  rootId: string,
  graphEdges: ReadonlyArray<{ source: string; target: string }>,
  opts: LensOptions = {},
): LensResult {
  const depth = opts.depth ?? DEFAULT_DEPTH;
  const down = lineageSet(index, rootId, "descendants", depth);

  const roleOf = (id: string): NodeRole => {
    if (id === rootId) return "sel";
    return down.has(id) ? "down" : "off";
  };

  // Sink-ish consumers (the prototype's `impactPlots`: downstream plots/runs).
  const isConsumerKind = (type: string): boolean =>
    /plot|figure|chart|report|view|envelope|run|case|solve|simulation|sample/.test(
      (type || "").toLowerCase(),
    );

  const summaryFor = (lit: Set<string>, dim: Set<string>): LensSummary => {
    const downstream = Math.max(0, down.size - 1);
    let plots = 0;
    for (const id of down) {
      if (id === rootId) continue;
      const t = index.nodeById.get(id)?.type ?? "";
      if (isConsumerKind(t)) plots += 1;
    }
    return {
      mode: "impact",
      lit: lit.size,
      dim: dim.size,
      detail: { downstream, plots },
      note: `${downstream} downstream — ${plots} would go stale if this changes`,
    };
  };

  return materialise("impact", index, graphEdges, roleOf, summaryFor);
}

// ─────────────────────────────────────────────────────────────────────────────
// diff lens — N-way set-intersection of ancestry across ≥2 picked artifacts.
//   shared  = in EVERY pick's lineage (inclusive of the pick) — the common src.
//   partial = in SOME but not all.
//   selpick = one of the picked artifacts itself.
// (The prototype's `inAllSel` / `inSomeSel`, generalised to N picks.)
// ─────────────────────────────────────────────────────────────────────────────

export function diffLens(
  index: GraphIndex,
  picks: ReadonlyArray<string>,
  graphEdges: ReadonlyArray<{ source: string; target: string }>,
  opts: LensOptions = {},
): LensResult {
  const depth = opts.depth ?? DEFAULT_DEPTH;
  // Dedupe + keep only real nodes; ancestry sets are INCLUSIVE of each pick.
  const validPicks = [...new Set(picks)].filter((id) => index.nodeById.has(id));
  const pickSet = new Set(validPicks);
  const ancestrySets = validPicks.map((id) => lineageSet(index, id, "ancestors", depth));

  const inAll = (id: string): boolean =>
    ancestrySets.length > 0 && ancestrySets.every((s) => s.has(id));
  const inSome = (id: string): boolean => ancestrySets.some((s) => s.has(id));

  const roleOf = (id: string): NodeRole => {
    if (pickSet.has(id)) return "selpick";
    if (inAll(id)) return "shared";
    if (inSome(id)) return "partial";
    return "off";
  };

  const summaryFor = (lit: Set<string>, dim: Set<string>, roleByNode: Map<string, NodeRole>): LensSummary => {
    let shared = 0;
    let partial = 0;
    for (const role of roleByNode.values()) {
      if (role === "shared") shared += 1;
      else if (role === "partial") partial += 1;
    }
    return {
      mode: "diff",
      lit: lit.size,
      dim: dim.size,
      detail: { shared, partial, picks: validPicks.length },
      note:
        validPicks.length < 2
          ? "pick at least two artifacts to find their common ancestry"
          : `${shared} shared source${shared === 1 ? "" : "s"} · ${partial} in only some`,
    };
  };

  return materialise("diff", index, graphEdges, roleOf, summaryFor);
}

// ─────────────────────────────────────────────────────────────────────────────
// filter lens — highlight one node KIND across the graph; dim the rest.
// (The prototype's `kindMatch`, including the `mesh` substring convenience.)
// ─────────────────────────────────────────────────────────────────────────────

/** Does `kind` match the filter? `mesh` is a substring convenience match. */
export function kindMatches(kind: string, filterKind: string | undefined): boolean {
  if (filterKind == null) return false;
  const k = (kind || "").toLowerCase();
  const f = filterKind.toLowerCase();
  if (k === f) return true;
  // `mesh` matches surface_mesh / volume_mesh / *mesh* (prototype parity).
  if (f === "mesh" && /mesh/.test(k)) return true;
  return false;
}

export function filterLens(
  index: GraphIndex,
  graphEdges: ReadonlyArray<{ source: string; target: string }>,
  opts: LensOptions = {},
): LensResult {
  const filterKind = opts.filterKind;

  const roleOf = (id: string): NodeRole => {
    const t = index.nodeById.get(id)?.type ?? "";
    return kindMatches(t, filterKind) ? "match" : "off";
  };

  const summaryFor = (lit: Set<string>, dim: Set<string>): LensSummary => ({
    mode: "filter",
    lit: lit.size,
    dim: dim.size,
    detail: { matched: lit.size, kind: filterKind ?? "—" },
    note: filterKind ? `${lit.size} ${filterKind} nodes highlighted` : "pick a kind to highlight",
  });

  return materialise("filter", index, graphEdges, roleOf, summaryFor);
}

// ─────────────────────────────────────────────────────────────────────────────
// Unified entry point — dispatch by mode. Keeps the renderer's call site small:
//   applyLens(index, edges, { mode:'trace', roots:[rootId], traceDirection })
// ─────────────────────────────────────────────────────────────────────────────

export interface ApplyLensArgs extends LensOptions {
  mode: DagLensMode;
  /**
   * The lens roots. trace/impact use `roots[0]`; diff uses ALL of them; filter
   * ignores roots entirely (it keys off `filterKind`).
   */
  roots: ReadonlyArray<string>;
}

export function applyLens(
  index: GraphIndex,
  graphEdges: ReadonlyArray<{ source: string; target: string }>,
  args: ApplyLensArgs,
): LensResult {
  const root = args.roots[0] ?? "";
  switch (args.mode) {
    case "impact":
      return impactLens(index, root, graphEdges, args);
    case "diff":
      return diffLens(index, args.roots, graphEdges, args);
    case "filter":
      return filterLens(index, graphEdges, args);
    case "trace":
    default:
      return traceLens(index, root, graphEdges, args);
  }
}
